import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../helpers/lpApi';

// ============================================================================
// Big Projects · Visit Fee — admin settings for the Large Projects visit fee.
// An enable/disable toggle + an amount input (USD). When disabled, the visit
// fee charge is skipped entirely at publish time regardless of amount.
//
// ALL operations run against besthand-api through the `/lp-graphql` proxy via
// the `lpApi` helper — NOT the web's own /graphql schema (no LP resolver is
// duplicated in besthand-web, per the architecture decision). Admin auth is
// bridged through the proxy as `authToken`; besthand-api's `requireAdmin`
// gates the updateProjectVisitFee mutation.
// ============================================================================

const Q_VISIT_FEE = `
  query {
    projectVisitFee {
      status errorMessage visitFeeAmount visitFeeEnabled
    }
  }
`;

const M_UPDATE = `
  mutation ($amountUsd: Float, $enabled: Boolean) {
    updateProjectVisitFee(amountUsd: $amountUsd, enabled: $enabled) {
      status errorMessage visitFeeAmount visitFeeEnabled
    }
  }
`;

// --- styles (inline, mirrors CompanyProviders.js / lightweight admin pages) ---
const S = {
  page: { maxWidth: 720, margin: '0 auto', padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h2: { margin: '0 0 4px', fontSize: 24, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 14, marginBottom: 20 },
  card: { border: '1px solid #e7e9ec', borderRadius: 14, padding: '22px 24px', marginBottom: 16, boxShadow: '0 6px 18px rgba(15,18,20,0.03)' },
  toggleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, paddingBottom: 18, borderBottom: '1px solid #eef1f3', marginBottom: 18 },
  label: { fontSize: 16, fontWeight: 800, margin: 0 },
  sub: { color: '#6b7280', fontSize: 13, marginTop: 4, lineHeight: 1.5 },
  switch: (on) => ({
    width: 52, height: 30, borderRadius: 999, border: 'none', cursor: 'pointer',
    background: on ? '#16a34a' : '#cbd2d8', position: 'relative', transition: 'background .15s',
    flexShrink: 0,
  }),
  knob: (on) => ({
    position: 'absolute', top: 3, left: on ? 25 : 3, width: 24, height: 24,
    borderRadius: '50%', background: '#fff', transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  }),
  amountWrap: { opacity: 1 },
  amountLabel: { display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 },
  inputRow: { display: 'flex', alignItems: 'center', gap: 0, maxWidth: 200 },
  dollar: { padding: '11px 12px', background: '#f6f8f9', border: '1px solid #d8dde2', borderRight: 'none', borderRadius: '9px 0 0 9px', fontSize: 15, fontWeight: 700, color: '#6b7280' },
  input: { flex: 1, padding: '10px 12px', border: '1px solid #d8dde2', borderRadius: '0 9px 9px 0', fontSize: 15, fontWeight: 600, outline: 'none', width: '100%', minWidth: 0 },
  inputHint: { color: '#9ca3af', fontSize: 12, marginTop: 6 },
  actions: { display: 'flex', gap: 10, marginTop: 22 },
  btn: { background: '#e8232b', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 22px', fontSize: 14, fontWeight: 800, cursor: 'pointer' },
  btnDisabled: { background: '#f0bcbe', cursor: 'not-allowed' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
  empty: { color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' },
};

function VisitFee() {
  const [enabled, setEnabled] = useState(true);
  const [amount, setAmount] = useState('25');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await lpApi(Q_VISIT_FEE);
      if (res.errors) {
        setMsg({ error: firstError(res) });
      } else {
        const env = res.data && res.data.projectVisitFee;
        if (env && env.status === 200) {
          setEnabled(env.visitFeeEnabled !== false);
          setAmount(env.visitFeeAmount != null ? String(env.visitFeeAmount) : '25');
        } else if (env) {
          setMsg({ error: env.errorMessage || `Error ${env.status}` });
        }
      }
    } catch (e) {
      setMsg({ error: `Could not load visit fee: ${e.message}` });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setMsg(null);
    const amt = Number(amount);
    if (Number.isNaN(amt) || amt < 0 || amt > 500) {
      setMsg({ error: 'Amount must be a number between $0 and $500.' });
      return;
    }
    setSaving(true);
    try {
      const res = await lpApi(M_UPDATE, { amountUsd: Math.round(amt * 100) / 100, enabled });
      if (res.errors) { setMsg({ error: firstError(res) }); setSaving(false); return; }
      const env = res.data && res.data.updateProjectVisitFee;
      if (env && env.status === 200) {
        setEnabled(env.visitFeeEnabled !== false);
        setAmount(env.visitFeeAmount != null ? String(env.visitFeeAmount) : amount);
        setMsg({ ok: 'Visit fee settings saved.' });
      } else {
        setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Save failed: ${e.message}` });
    }
    setSaving(false);
  };

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Visit Fee</h2>
      <div style={S.hint}>
        Large Projects — the visit fee charged to a client when they publish a project request.
        Turn it off to skip the charge entirely, or set the amount (USD).
      </div>

      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}

      {loading ? (
        <div style={S.empty}>Loading…</div>
      ) : (
        <div style={S.card}>
          <div style={S.toggleRow}>
            <div>
              <p style={S.label}>Charge a visit fee</p>
              <div style={S.sub}>
                {enabled
                  ? 'Clients are charged the amount below when they publish a project.'
                  : 'Disabled — no visit fee is charged at publish, regardless of amount.'}
              </div>
            </div>
            <button
              type="button"
              style={S.switch(enabled)}
              onClick={() => setEnabled((v) => !v)}
              aria-pressed={enabled}
              aria-label="Toggle visit fee"
            >
              <span style={S.knob(enabled)} />
            </button>
          </div>

          <div style={{ ...S.amountWrap, opacity: enabled ? 1 : 0.5 }}>
            <label style={S.amountLabel} htmlFor="visit-fee-amount">Amount (USD)</label>
            <div style={S.inputRow}>
              <span style={S.dollar}>$</span>
              <input
                id="visit-fee-amount"
                style={S.input}
                type="number"
                min="0"
                max="500"
                step="0.01"
                value={amount}
                disabled={!enabled}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div style={S.inputHint}>Between $0 and $500. Saved even while disabled, applied when re-enabled.</div>
          </div>

          <div style={S.actions}>
            <button
              type="button"
              style={saving ? { ...S.btn, ...S.btnDisabled } : S.btn}
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default VisitFee;
